// Sprint UI-7b (D76) — chat-header chip showing "this branch is ↑N ↓M
// commits vs origin/main". Distinct from BranchChip's own ahead/behind
// counters which track the branch's upstream (@{u}).
//
// Render contract:
//   `↑N ↓M`  (both if both > 0, one if only one > 0)
//   null     (both 0 or both null — the chip hides).
//
// Same styling language as BranchChip: caption-sized JetBrains Mono
// numerals, fg-subtle color, tabular-nums for stable alignment.

import type { GitStatus } from '../types';
import { useTranslation } from '../i18n';

export interface AheadBehindChipProps {
  gitStatus: GitStatus | null | undefined;
}

export function AheadBehindChip({ gitStatus }: AheadBehindChipProps) {
  const { t } = useTranslation();
  if (!gitStatus) return null;
  const { aheadBy, behindBy } = gitStatus;
  const a = aheadBy ?? 0;
  const b = behindBy ?? 0;
  if (a === 0 && b === 0) return null;

  const tooltipParts: string[] = [];
  if (a > 0) tooltipParts.push(t('branch.aheadOfMain', { count: a }));
  if (b > 0) tooltipParts.push(t('branch.behindOfMain', { count: b }));

  return (
    <span
      className="inline-flex items-center gap-1 shrink-0 font-mono text-caption tabular-nums text-muted-foreground/70"
      data-testid="ahead-behind-chip"
      title={tooltipParts.join(' · ')}
    >
      {a > 0 && (
        <span aria-label={t('branch.aheadOfMain', { count: a })}>
          {/* eslint-disable-next-line i18n-enforce/no-literal-strings-in-jsx -- git glyph */}
          {'↑'}
          {a}
        </span>
      )}
      {b > 0 && (
        <span aria-label={t('branch.behindOfMain', { count: b })}>
          {/* eslint-disable-next-line i18n-enforce/no-literal-strings-in-jsx -- git glyph */}
          {'↓'}
          {b}
        </span>
      )}
    </span>
  );
}
